import { Plugin } from 'chart.js';
import { DataPoint } from 'src/app/api/generated';

export interface Filter {
  enabled: boolean;
  from: string;
  till: string;
}

export const MOVEMENT_SCATTER_FIELDS = ['dl', 'dq'];

export interface UserSettings {
  start_date: string;
  end_date: string;
}

export interface Datasource {
  id: string;
  name: string;
  checked: boolean;
}

export interface GenericMovementScatterplotConfig {
  guid: string;
  type: string;
  headline: string;
  dataset: Datasource[];
  filter: Filter;
  user_settings: UserSettings;
  bg_image_resource_url: string;
}

export const ChartJSImagePlugin = (imageSource: string): Plugin<'scatter'> => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = imageSource;
  return {
    id: 'lumoe_image_viewer_plugin',
    beforeDraw: (chart, args, options) => {
      if (image.complete) {
        const ctx = chart.ctx;
        const { top, left, width, height } = chart.chartArea;
        const newDimensions = getImageDimensions(image, chart.height - 70);
        image.width = newDimensions.width;
        image.height = newDimensions.height;
        const x = left + width / 2 - image.width / 2;
        const y = top + height / 2 - image.height / 2;
        ctx.drawImage(image, x, y, newDimensions.width, newDimensions.height);
      } else {
        image.onload = () => chart.draw();
      }
    },
  };
};

function getImageDimensions(image: HTMLImageElement, maxHeight: number) {
  const ratio = maxHeight / image.height;
  return {
    width: image.width * ratio,
    height: image.height * ratio,
  };
}

export interface MinMaxValue {
  id: string;
  min: number;
  max: number;
}

export interface DataPointGnss {
  epoch: Date;
  dx?: number | null;
  dy?: number | null;
  dz?: number | null;
  dl?: number | null;
  dq?: number | null;
  d2d?: number | null;
  d3d?: number | null;
}

export function parseGNSSData(data: DataPoint[]): DataPointGnss[] {
  return data.map((value) => {
    return {
      epoch: new Date(value.epoch),
      dx: Number(value.dx),
      dy: Number(value.dy),
      dz: Number(value.dz),
      dl: Number(value.dl),
      dq: Number(value.dq),
      d2d: Number(value.d2d),
      d3d: Number(value.d3d),

      dy_k: Number(value.dy_k),
      dx_k: Number(value.dx_k),
      dz_k: Number(value.dz_k),
      d2d_k: Number(value.d2d_k),
      dl_k: Number(value.dl_k),
      dq_k: Number(value.dq_k),
      d3d_k: Number(value.d3d_k),
      vl: Number(value.vl),
      vq: Number(value.vq),
      vz: Number(value.vz),
    };
  });
}

const oneDay = 1000 * 60 * 60 * 24;
const oneHour = 1000 * 60 * 60;

export function getSelectableTimePeriods(translateService?: any) {
  return [
    {
      viewValue:
        translateService?.instant('app.timePeriodLast24Hours') ||
        'letzte 24 Stunden',
      value: new Date(Date.now() - oneDay),
    },
    {
      viewValue:
        translateService?.instant('app.timePeriodLastWeek') || 'letzte Woche',
      value: new Date(Date.now() - oneDay * 7),
    },
    {
      viewValue:
        translateService?.instant('app.timePeriodLast2Weeks') ||
        'letzte 2 Wochen',
      value: new Date(Date.now() - oneDay * 14),
    },
    {
      viewValue:
        translateService?.instant('app.timePeriodLastMonth') || 'letztes Monat',
      value: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    },
    {
      viewValue:
        translateService?.instant('app.timePeriodLastYear') || 'letztes Jahr',
      value: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
    },
    {
      viewValue:
        translateService?.instant('app.timePeriodFromZeroMeasurement') ||
        'Von Nullmessung',
      value: new Date(0),
    },
    {
      viewValue:
        translateService?.instant('app.timePeriodSelectRange') ||
        'Zeitbereich auswählen',
      value: -1,
    },
  ];
}

// Keep backward compatibility - will use default German
export const selectableTimePeriods = getSelectableTimePeriods();

export function getSelectableLowPassFilterPeriods(translateService?: any) {
  return [
    {
      viewValue:
        translateService?.instant('app.smoothingNone') || 'Keine Glättung',
      value: new Date(0),
    },
    {
      viewValue:
        translateService?.instant('app.smoothing12Hours') || '12 Stunden',
      value: new Date(Date.now() - oneHour * 12),
    },
    {
      viewValue: translateService?.instant('app.smoothing1Day') || '1 Tag',
      value: new Date(Date.now() - oneDay),
    },
    {
      viewValue: translateService?.instant('app.smoothing1Week') || '1 Woche',
      value: new Date(Date.now() - oneDay * 7),
    },
    {
      viewValue: translateService?.instant('app.smoothing2Weeks') || '2 Wochen',
      value: new Date(Date.now() - oneDay * 14),
    },
    {
      viewValue: translateService?.instant('app.smoothing1Month') || '1 Monat',
      value: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    },
  ];
}

// Keep backward compatibility - will use default German
export const selectableLowPassFilterPeriods =
  getSelectableLowPassFilterPeriods();

export function getMinMaxFromConfig(
  fieldName: string,
  config: MinMaxValue[]
): MinMaxValue {
  if (!config) {
    config = [];
  }
  const minMaxArr = config.filter((field) => {
    if (field.id == fieldName) {
      return true;
    }
  });

  if (minMaxArr.length === 0) {
    return <MinMaxValue>{
      id: fieldName,
      min: 0,
      max: 0,
    };
  }

  return minMaxArr[0];
}

export function getParameterDisplayName(
  parameter: string,
  translateService?: any
): string {
  const translations: { [key: string]: string } = {
    dl: translateService?.instant('app.parameterLongitudinal') || 'Längs',
    dq: translateService?.instant('app.parameterTransverse') || 'Quer',
    dz: translateService?.instant('app.parameterHeight') || 'Höhe',
    d2d: translateService?.instant('app.displacement2D') || '2D-Verschiebung',
    d3d: translateService?.instant('app.displacement3D') || '3D-Verschiebung',
    d2d_k:
      translateService?.instant('app.displacement2DKalman') ||
      '2D-Verschiebung Kalman gefiltert',
    d3d_k:
      translateService?.instant('app.displacement3DKalman') ||
      '3D-Verschiebung Kalman gefiltert',
    dl_k:
      translateService?.instant('app.displacementLongitudinalKalman') ||
      'Verschiebung Längs Kalman gefiltert',
    dq_k:
      translateService?.instant('app.displacementTransverseKalman') ||
      'Verschiebung Quer Kalman gefiltert',
    dz_k:
      translateService?.instant('app.displacementHeightKalman') ||
      'Verschiebung Höhe Kalman gefiltert',
    vl:
      translateService?.instant('app.velocityLongitudinal') ||
      'Geschwindigkeit Längs',
    vq:
      translateService?.instant('app.velocityTransverse') ||
      'Geschwindigkeit Quer',
    vz:
      translateService?.instant('app.velocityHeight') || 'Geschwindigkeit Höhe',
  };
  return translations[parameter] || parameter;
}


export function round(value: number | string, precision: number) {
  let parsedValue: number;
  if (typeof value === 'string') {
    parsedValue = parseFloat(value);
  } else {
    parsedValue = value;
  }
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(parsedValue * multiplier) / multiplier;
}
